// src/Footer.js
import React, { useEffect, useRef } from "react";
import { Link } from "./utils/localizedNav";
import { useTranslation } from "react-i18next";
import { onSentryReady } from "./sentry";
import { fonts, colors, layout, gradients } from "./designTokens";
import { resetConsent } from "./consent";
import useIsMobile from "./hooks/useIsMobile";

export default function Footer() {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const reportBugRef = useRef(null);

  // Sentry User Feedback Widget: nur aktiv, wenn REACT_APP_SENTRY_DSN gesetzt ist
  // (Sentry.init() in index.js ist sonst komplett No-Op, getFeedback() liefert dann
  // undefined). autoInject:false im Init → Trigger wird hier an den eigenen,
  // ins Seitendesign passenden Button gehängt statt Sentrys Standard-Widget zu zeigen.
  // Re-Attach bei Sprachwechsel, damit das Popup in der aktuell gewählten Sprache bleibt.
  // Sentry wird verzögert geladen → Anbindung erst, sobald es bereit ist.
  useEffect(() => {
    let detach;
    const unsubscribe = onSentryReady((Sentry) => {
      const feedback = Sentry.getFeedback?.();
      if (!feedback || !reportBugRef.current) return;
      detach = feedback.attachTo(reportBugRef.current, {
        formTitle: t("footer.reportBug.formTitle"),
        messageLabel: t("footer.reportBug.messageLabel"),
        messagePlaceholder: t("footer.reportBug.messagePlaceholder"),
        submitButtonLabel: t("footer.reportBug.submit"),
        successMessageText: t("footer.reportBug.success"),
      });
    });
    return () => { unsubscribe(); if (typeof detach === "function") detach(); };
  }, [i18n.language, t]);

  const colHeaderStyle = {
    fontWeight: 800,
    fontSize: 11,
    letterSpacing: 1.3,
    textTransform: "uppercase",
    marginBottom: 10,
    color: colors.amberDark,
    paddingBottom: 6,
    borderBottom: "1.5px solid rgba(160,120,0,0.18)",
    display: "block",
  };

  const linkStyle = {
    color: colors.amberText,
    textDecoration: "none",
    fontWeight: 500,
    fontSize: isMobile ? 13 : 13.5,
    // Mobil: Mindest-Tippfläche 24 px (WCAG 2.5.8) – vorher lagen die Links zu dicht
    ...(isMobile ? { display: "inline-block", minHeight: 24, lineHeight: "24px" } : {}),
  };

  return (
    <footer
      style={{
        width: "100%",
        background: gradients.footer,
        padding: isMobile ? "24px 16px 10px" : "36px 0 12px 0",
        fontFamily: fonts.body,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          maxWidth: layout.maxWidth,
          margin: "0 auto",
          padding: isMobile ? "0" : "0 18px",
          boxSizing: "border-box",
        }}
      >
        {/* Row 1: Brand + Donate + Social */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 12 : 18,
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            paddingBottom: isMobile ? 16 : 18,
            borderBottom: "1.5px solid rgba(204,186,116,0.5)",
          }}
        >
          {/* Brand + Claim */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                fontFamily: fonts.heading,
                fontSize: isMobile ? 18 : 22,
                letterSpacing: 0.8,
                color: colors.amberDark,
                textShadow: "0 1px 3px #fff9d4",
                whiteSpace: "nowrap",
              }}>
                💨 Art-of-Fart.com
              </span>
            </div>
            <span style={{ fontSize: isMobile ? 12.5 : 13, color: colors.amberMuted, maxWidth: 360 }}>
              {t("footer.tagline")}
            </span>
          </div>

          {/* Donate + Social */}
          <div style={{
            display: "flex",
            flexDirection: isMobile ? "column-reverse" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 10 : 16,
          }}>
            {/* Social links als Chip-Buttons */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <a
                href="https://www.instagram.com/art_of_fart.com_official"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-chip"
                style={{
                  color: "#b24b8a",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: isMobile ? 13 : 13.5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  border: "1.5px solid #b24b8a",
                  borderRadius: 999,
                  padding: "4px 12px",
                  background: "rgba(255,255,255,0.6)",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
              >
                <span>📸</span>
                <span>{t("footer.social.instagram")}</span>
              </a>
              <a
                href="https://discord.gg/zczG62P89S"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-chip"
                style={{
                  color: "#4267d1",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: isMobile ? 13 : 13.5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  border: "1.5px solid #4267d1",
                  borderRadius: 999,
                  padding: "4px 12px",
                  background: "rgba(255,255,255,0.6)",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
              >
                <span>💬</span>
                <span>{t("footer.social.discord")}</span>
              </a>
            </div>

            {/* Donate */}
            <form
              action="https://www.paypal.com/donate"
              method="post"
              target="_top"
              style={{ margin: 0, display: "flex", alignItems: "center" }}
            >
              <input type="hidden" name="hosted_button_id" value="HTY6HV6F8THCU" />
              <button
                type="submit"
                title="Donate with PayPal"
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: isMobile ? "7px 16px" : "8px 18px",
                  fontWeight: 700,
                  fontSize: isMobile ? 13 : 13.5,
                  cursor: "pointer",
                  background: gradients.donateBtn,
                  color: colors.amberDark,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  fontFamily: fonts.body,
                  whiteSpace: "nowrap",
                  transition: "box-shadow 0.15s, transform 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.22)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)"; e.currentTarget.style.transform = "translateY(0)"; }}
                className="donate-btn"
              >
                <span className="donate-icon">☕</span> {t("footer.donate")}
              </button>
            </form>
          </div>
        </div>

        {/* Row 2: Nav-Columns */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : "1.2fr 1fr 1fr",
            gap: isMobile ? 16 : 22,
            paddingTop: isMobile ? 16 : 18,
            paddingBottom: isMobile ? 10 : 12,
          }}
        >
          {/* Column 1: Explore */}
          <div>
            <span style={colHeaderStyle}>{t("footer.section.explore")}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {[
                { to: "/", label: t("nav.home") },
                { to: "/gallery", label: t("nav.gallery") },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="footer-link" style={linkStyle}>{label}</Link>
              ))}
              <Link
                to="/"
                className="footer-link"
                style={linkStyle}
                onClick={() => setTimeout(() => {
                  const el = document.getElementById("fart-upload-section");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }, 100)}
              >
                {t("nav.upload")}
              </Link>
              {[
                { to: "/users",        label: t("nav.users") },
                { to: "/map",          label: `🌍 ${t("nav.map")}` },
                { to: "/encyclopedia", label: `📚 ${t("nav.encyclopedia")}` },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="footer-link" style={linkStyle}>{label}</Link>
              ))}
            </div>
          </div>

          {/* Column 2: Help / Info */}
          <div>
            <span style={colHeaderStyle}>{t("footer.section.help")}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {[
                { to: "/faq",       label: t("footer.faqHelp") },
                { to: "/contact",   label: t("footer.contact") },
                { to: "/impressum", label: t("footer.imprint") },
                { to: "/privacy",   label: t("footer.privacy") },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="footer-link" style={linkStyle}>{label}</Link>
              ))}
              {/* Cookie-Einstellungen: gleiche Gewichtung wie andere Links */}
              <button
                onClick={() => {
                  resetConsent();
                  window.location.reload();
                }}
                className="footer-link"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: colors.amberText,
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  fontSize: isMobile ? 13 : 13.5,
                  textAlign: "left",
                }}
              >
                {t("cookie.settings")}
              </button>
              {/* Sentry User Feedback Widget hängt sich hier per attachTo() ein (s.o.) */}
              <button
                ref={reportBugRef}
                className="footer-link"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: colors.amberText,
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  fontSize: isMobile ? 13 : 13.5,
                  textAlign: "left",
                }}
              >
                {t("footer.reportBug")}
              </button>
            </div>
          </div>

          {/* Column 3: Encyclopedia (SEO) — nur Desktop */}
          <div style={{ display: isMobile ? "none" : "block" }}>
            <span style={colHeaderStyle}>{t("footer.section.encyclopedia")}</span>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {[
                { to: "/encyclopedia/what-is-flatulence",    label: t("footer.enc.what-is-flatulence") },
                { to: "/encyclopedia/why-farts-smell",       label: t("footer.enc.why-farts-smell") },
                { to: "/encyclopedia/fart-chemistry",        label: t("footer.enc.fart-chemistry") },
                { to: "/encyclopedia/fart-world-records",    label: t("footer.enc.fart-world-records") },
                { to: "/encyclopedia/animals-that-fart",     label: t("footer.enc.animals-that-fart") },
                { to: "/encyclopedia/fart-sounds-acoustics", label: t("footer.enc.fart-sounds-acoustics") },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="footer-link" style={linkStyle}>{label}</Link>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Copyright */}
        <div
          style={{
            borderTop: "1px solid rgba(204,186,116,0.45)",
            paddingTop: 8,
            marginTop: isMobile ? 4 : 2,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: 4,
            fontSize: isMobile ? 11.5 : 12.5,
            color: colors.amberSubtle,
          }}
        >
          <span>{t("footer.bottom.left", { year })}</span>
          <span>{t("footer.bottom.right")}</span>
        </div>
      </div>

      <style>{`
        .footer-link:hover { color: #c59524 !important; text-decoration: underline !important; }
        .footer-link:active { color: #a07010 !important; opacity: 0.75 !important; }
        .footer-social-chip:hover { background: rgba(255,255,255,0.9) !important; box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important; }
        .footer-social-chip:active { opacity: 0.7 !important; }
        @keyframes coffeeWobble {
          0%,100% { transform: rotate(0deg); }
          25% { transform: rotate(-12deg); }
          75% { transform: rotate(12deg); }
        }
        .donate-btn:hover .donate-icon { animation: coffeeWobble 0.4s ease; }
      `}</style>
    </footer>
  );
}
